import type { MouseEvent, ReactNode } from "react"
import { FACEBOOK_FEED_ASPECT_CLASS, MediaPreview, cn } from "@nexus/ui"
import type { CarouselSlide } from "@/features/opportunities/types/opportunities.types"

export interface DrilldownCreativeCardShellProps {
  thumbnailUrl: string
  assetUrl: string
  isVideo: boolean
  isCarousel: boolean
  carouselSlides?: CarouselSlide[]
  alt: string
  title: string
  /** Logo shown next to the title in the lightbox footer — matches the list/chart views. */
  logoSrc?: string
  lightboxMeta?: ReactNode
  /** Absolutely-positioned badges/avatar/center-icon — assembled by the caller
   *  since only the Competitors card has an avatar. */
  mediaOverlays: ReactNode
  /** Stat rows + calendar/active-between row — assembled by the caller since row
   *  count/content differs per mode. */
  statsChildren: ReactNode
  /** When set, the stats block opens the creative detail drawer on click. */
  onStatsClick?: () => void
  /** When set, a click anywhere on the card fires it, and the media preview moves to its own button. */
  onCardClick?: () => void
}

/**
 * Shared shell for the redesigned Drilldown grid card: media block (thumbnail/
 * video/carousel via MediaPreview) + carousel mini-strip (reskinned from the
 * Opportunities CreativeCard's proven pattern) + a bordered stats block. Never
 * touches the Opportunities-owned CreativeCard/VirtualizedCreativeGrid directly.
 */
export function DrilldownCreativeCardShell({
  thumbnailUrl,
  assetUrl,
  isVideo,
  isCarousel,
  carouselSlides,
  alt,
  title,
  logoSrc,
  lightboxMeta,
  mediaOverlays,
  statsChildren,
  onStatsClick,
  onCardClick,
}: DrilldownCreativeCardShellProps) {
  const handleCardClick = onCardClick
    ? (event: MouseEvent<HTMLDivElement>) => {
        // React bubbles clicks out of portals (the open preview lightbox) through the tree.
        if (event.currentTarget.contains(event.target as Node)) onCardClick()
      }
    : undefined

  return (
    <div
      className={cn(
        "group w-full overflow-hidden rounded-md border border-border bg-card shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)]",
        onCardClick && "cursor-pointer",
      )}
      onClick={handleCardClick}
    >
      <MediaPreview
        src={assetUrl || undefined}
        thumbnailSrc={thumbnailUrl}
        type={isVideo ? "video" : "image"}
        alt={alt}
        title={title}
        logoSrc={logoSrc}
        meta={lightboxMeta}
        slides={carouselSlides}
        previewTrigger={onCardClick ? "button" : "thumbnail"}
        className={`${FACEBOOK_FEED_ASPECT_CLASS} overflow-hidden bg-muted`}
      >
        {mediaOverlays}
      </MediaPreview>

      {isCarousel && carouselSlides && carouselSlides.length > 1 ? (
        <div className="flex gap-1 overflow-x-auto px-1.5 pt-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {carouselSlides.slice(1).map((slide) => (
            <img
              key={slide.hash}
              src={slide.thumbnailUrl}
              alt=""
              aria-hidden="true"
              className="size-7 shrink-0 rounded object-cover"
            />
          ))}
        </div>
      ) : null}

      <div
        className={onStatsClick ? "flex cursor-pointer flex-col border-t border-border pt-px transition-colors hover:bg-muted/30" : "flex flex-col border-t border-border pt-px"}
        data-testid={onStatsClick ? "drilldown-card-stats" : undefined}
        onClick={onStatsClick}
        onKeyDown={
          onStatsClick
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  onStatsClick()
                }
              }
            : undefined
        }
        role={onStatsClick ? "button" : undefined}
        tabIndex={onStatsClick ? 0 : undefined}
      >
        {statsChildren}
      </div>
    </div>
  )
}
